import { useState, useCallback } from 'react'
import type { FullPokemonCard, Locale } from '@/types/pokemon'
import { getApiUrl } from '@/lib/utils'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { STANDARD_CONDITIONS, type StandardCondition } from '@/types/inventory'
import { Loader2, Search, X, ImageIcon } from 'lucide-react'

interface CardSearchModalProps {
  isOpen: boolean
  onClose: () => void
  onAdd: (cardId: string, condition: StandardCondition, price: number, locale: Locale) => Promise<void>
}

export function CardSearchModal({ isOpen, onClose, onAdd }: CardSearchModalProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<FullPokemonCard[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedCard, setSelectedCard] = useState<FullPokemonCard | null>(null)
  const [condition, setCondition] = useState<StandardCondition>('NearMint')
  const [price, setPrice] = useState<number>(0)
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const API_URL = getApiUrl()

  const doSearch = useCallback(async () => {
    const term = query.trim()
    if (!term) {
      setResults([])
      return
    }
    setIsSearching(true)
    setError(null)
    try {
      const res = await fetch(`${API_URL}/api/search?q=${encodeURIComponent(term)}`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Search failed')
      const data = await res.json()
      setResults(Array.isArray(data) ? data : [])
      setSelectedCard(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed')
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }, [query, API_URL])

  const handleAdd = async () => {
    if (!selectedCard) return
    setIsAdding(true)
    setError(null)
    try {
      await onAdd(selectedCard.id, condition, price, selectedCard.set.locale ?? 'en')
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card')
    } finally {
      setIsAdding(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleSelectCard = (card: FullPokemonCard) => {
    setSelectedCard(card)
    setPrice(card.cardMarketPrice?.avg ?? 0)
    setCondition('NearMint')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={handleBackdropClick}>
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-hidden my-8 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 m-0">Search cards</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          <div className="flex gap-2">
            <Input
              placeholder="Search by card name..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), doSearch())}
              className="flex-1"
              autoFocus
            />
            <Button onClick={doSearch} disabled={isSearching}>
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {results.length > 0 && (
            <div className="border border-gray-200 rounded-lg divide-y max-h-60 overflow-y-auto">
              {results.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => handleSelectCard(card)}
                  className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors ${
                    selectedCard?.id === card.id ? 'bg-blue-50 ring-1 ring-blue-200' : ''
                  }`}
                >
                  <div className="w-12 h-16 rounded overflow-hidden bg-muted border shrink-0 flex items-center justify-center">
                    {card.cardData.imageUrl ? (
                      <img src={card.cardData.imageUrl} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{card.name}</p>
                    {card.set && <p className="text-sm text-muted-foreground truncate">{card.set.name}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedCard && (
            <div className="p-4 bg-gray-50 rounded-lg border space-y-4">
              <p className="font-medium text-gray-900">Add &quot;{selectedCard.name}&quot;</p>
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label className="text-sm">Condition</Label>
                  <Select value={condition} onValueChange={(v) => setCondition(v as StandardCondition)}>
                    <SelectTrigger className="h-8 w-[120px] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STANDARD_CONDITIONS.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Max price ($)</Label>
                  <Input
                    type="text"
                    value={price}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === '' || /^\d*\.?\d*$/.test(v)) setPrice(Number(v))
                    }}
                    placeholder="0.00"
                    className="w-20 h-8 mt-1 text-sm"
                  />
                </div>
                <Button onClick={handleAdd} disabled={isAdding} className="bg-blue-800 text-white hover:bg-blue-900">
                  {isAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add to offer'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
